import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiImageGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-image-generator',
			name: 'AI Image Generator — Create AI Images Online by Lift',
			description:
				'Generate AI images from any prompt. Pick a model, style, and ratio, and get high-quality results in seconds — or run several models at once and compare. Try Lift today!',
			url: 'https://liftapp.ai/ai-image-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-image-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-image-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-image-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-image-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-image-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-image-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-image-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-image-generator#product',
			name: 'AI Image Generator',
			description:
				'Generate AI images from any prompt using leading AI models inside Lift — pick a model, style, and ratio for high-quality results in seconds.',
			url: 'https://liftapp.ai/ai-image-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-image-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Write your prompt',
					text: 'Describe the image you want in any language — or tap Improve Prompt and let AI turn your idea into a richer, more detailed description',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Pick a model, style, and settings',
					text: 'Choose an AI model, select a visual style, and set the aspect ratio, quality, and number of images for your run — or add a reference photo to guide the result',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Generate and compare',
					text: 'Lift renders your request in moments — select several models at once and see how each one interprets the same prompt',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-generator#benefits',
			name: 'Who can benefit from AI Image Generator?',
			description:
				"Content creators, marketers, designers, small business owners, and artists can benefit from Lift's AI Image Generator for creating unique visuals from any prompt.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Produce scroll-stopping AI pictures for Instagram, TikTok, and YouTube in seconds. Regenerate any result to get fresh takes on the same idea without photoshoots or design tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Create ad creatives, banners, and landing page graphics at scale. Run the same brief through different AI models and instantly see which direction sells the concept best',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers & freelancers — Draft concepts and explore visual directions in minutes. Remix a promising result to keep iterating on it instead of starting every version from scratch',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small business owners — Get product mockups, promo images, and branded graphics without hiring a designer. Full control over model, style, and ratio means professional results with zero design experience',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Artists & AI art enthusiasts — Experiment with styles, prompts, and batches of variations to push your creative boundaries. Your generations are saved in one gallery, ready to revisit, remix, or download',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Image Generator Examples by Lift',
				description:
					'Examples of AI generated images created with the Lift AI image generator.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-image-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Image Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Image Generator is a premium online tool that turns your prompt into unique, high-quality images. It brings together leading AI models — including Nano Banana, GPT Image, Flux, and Seedream — so you can always pick the best AI image generator for the task at hand.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Image Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Simply write your prompt — or let Improve Prompt polish it for you — then choose a model, style, ratio, and quality, and optionally add a reference image. Hit Generate and receive your AI-created picture in seconds — no design experience needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I generate images with several AI models at once?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift lets you select multiple models for a single prompt and fills a results grid as each one finishes. Every image is labeled with the model that made it, so comparing and keeping the strongest version takes seconds.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-generator#why-lift',
			name: "Why Lift's AI Image Generator stands out",
			description:
				"Lift's AI Image Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Best AI Image Generator by Lift',
				description: 'Best AI for image generation — Lift.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-image-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Image Generator',
					item: 'https://liftapp.ai/ai-image-generator',
				},
			],
		},
	],
};
